/**
 * Workspace roles and the capability matrix from the requirements spec (§4).
 *
 * Every API operation resolves the caller's role and checks it against `can()`.
 */

export const ROLES = ['admin', 'editor', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

/**
 * Capabilities are the unit of authorization. Handlers name a capability;
 * they never test a role directly.
 */
export const CAPABILITIES = [
  'quiz:read',
  'quiz:write',
  'quiz:delete',
  'quiz:present',
  'results:reset',
  'questions:moderate',
  'member:manage',
  'workspace:manage',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const VIEWER: Capability[] = ['quiz:read'];
const EDITOR: Capability[] = [
  ...VIEWER,
  'quiz:write',
  'quiz:delete',
  'quiz:present',
  'results:reset',
  // Audience Q&A (#118). A capability rather than a fourth role: "moderator"
  // is a job someone does for one evening, and a role ripples through
  // `Member`, invitations, the role picker and every ROLES enumeration. It
  // sits with the editor set because whoever may run the room is who decides
  // what reaches the projector — and deliberately not with `quiz:read`, so a
  // viewer never sees the pending queue, which is the one place unmoderated
  // audience text exists.
  'questions:moderate',
];
const ADMIN: Capability[] = [...EDITOR, 'member:manage', 'workspace:manage'];

const CAPABILITIES_BY_ROLE: Record<Role, readonly Capability[]> = {
  viewer: VIEWER,
  editor: EDITOR,
  admin: ADMIN,
};

export function capabilitiesFor(role: Role): readonly Capability[] {
  return CAPABILITIES_BY_ROLE[role];
}

export function can(role: Role, capability: Capability): boolean {
  return CAPABILITIES_BY_ROLE[role].includes(capability);
}
